import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Trash2, Eye, Inbox } from "lucide-react";
import Seo from "@/components/Seo";
import { ENQUIRY_STATUSES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vbs_admin_token";

const STATUS_BADGE = {
  New: "bg-blue-50 text-blue-700",
  Contacted: "bg-amber-50 text-amber-700",
  "In Review": "bg-violet-50 text-violet-700",
  Completed: "bg-emerald-50 text-emerald-700",
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Welcome back, Admin!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 max-w-md mx-auto py-20" data-testid="admin-login">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-8">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center rounded-2xl font-heading font-extrabold mb-5 shadow-md shadow-orange-500/30">
          VBS
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Portal</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">Sign in to manage project enquiries.</p>
        {error && (
          <div data-testid="admin-login-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
            {error}
          </div>
        )}
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Email</label>
        <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mb-4" />
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Password</label>
        <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mb-6" />
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold py-3 rounded-full hover:from-orange-600 hover:to-amber-600 transition-colors duration-200 disabled:opacity-60 shadow-lg shadow-orange-500/25"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setToken(null);
    setEnquiries(null);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, authHeaders());
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error("Could not load enquiries.");
      }
    }
  }, [authHeaders, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, authHeaders());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Status updated to "${status}"`);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Status update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, authHeaders());
      setEnquiries((list) => list.filter((e) => e.id !== id));
      setSelected(null);
      toast.success("Enquiry deleted.");
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Delete failed.");
    }
  };

  const filtered = useMemo(() => {
    if (!enquiries) return [];
    const q = search.trim().toLowerCase();
    return enquiries.filter((e) => {
      if (filter !== "All" && e.status !== filter) return false;
      if (!q) return true;
      return [e.name, e.phone, e.email, e.service, e.message].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [enquiries, filter, search]);

  if (!token) {
    return (
      <div data-testid="admin-page">
        <Seo title="Admin — V.B. Sharada Associates" description="Admin portal." />
        <LoginForm onLogin={setToken} />
      </div>
    );
  }

  const counts = { All: enquiries?.length || 0 };
  ENQUIRY_STATUSES.forEach((s) => (counts[s] = enquiries?.filter((e) => e.status === s).length || 0));

  return (
    <div data-testid="admin-page" className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-10 md:py-14">
      <Seo title="Admin — V.B. Sharada Associates" description="Admin portal." />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-blue-600 font-semibold mb-1">Admin Portal</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Enquiries</h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-sm text-sm hover:border-blue-800 hover:text-blue-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-4 py-2 rounded-sm text-sm hover:bg-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
          {["All", ...ENQUIRY_STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(s)}
              className={`px-3.5 py-2 text-xs font-semibold rounded-full border transition-colors ${
                filter === s ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/25" : "bg-white text-slate-600 border-slate-200 hover:border-orange-400"
              }`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>
        <div className="relative md:ml-auto md:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, service..."
            className="pl-9"
          />
        </div>
      </div>

      {enquiries === null ? (
        <p className="text-slate-500 text-sm">Loading enquiries...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="admin-empty-state">
          <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No enquiries found.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="admin-enquiry-table">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Date</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Name</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Phone</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Service</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Status</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-enquiry-row-${e.id}`}>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(e.created_at)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{e.name}</td>
                  <td className="px-4 py-3 text-slate-600">{e.phone}</td>
                  <td className="px-4 py-3 text-slate-600">{e.service || "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                      <SelectTrigger data-testid={`admin-status-select-${e.id}`} className={`h-8 w-32 text-xs font-semibold ${STATUS_BADGE[e.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENQUIRY_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        data-testid={`admin-view-${e.id}`}
                        onClick={() => setSelected(e)}
                        className="p-2 text-slate-500 hover:text-blue-800"
                        aria-label="View enquiry"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-delete-${e.id}`}
                        onClick={() => remove(e.id)}
                        className="p-2 text-slate-500 hover:text-red-600"
                        aria-label="Delete enquiry"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="admin-enquiry-detail-modal">
          <DialogHeader>
            <DialogTitle>Enquiry from {selected?.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="font-semibold text-slate-700">Phone:</span> <span className="text-slate-600">{selected.phone}</span></div>
              <div><span className="font-semibold text-slate-700">Email:</span> <span className="text-slate-600">{selected.email || "—"}</span></div>
              <div><span className="font-semibold text-slate-700">Service:</span> <span className="text-slate-600">{selected.service || "—"}</span></div>
              <div><span className="font-semibold text-slate-700">Received:</span> <span className="text-slate-600">{formatDate(selected.created_at)}</span></div>
              <div>
                <span className="font-semibold text-slate-700">Message:</span>
                <p className="mt-1 text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3 whitespace-pre-wrap">
                  {selected.message || "—"}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
